import type { AuctisyncSupabase } from './client.js';

/**
 * Where a lot's bids go (2026-10-07, migration 20261007140000).
 *
 * ONE BOOK PER SALE. A sale's bids live either on the auction engine or in
 * `place_bid`'s ledger, never both, and the database decides which:
 * `public.get_bid_path(lot)` answers the path, the sale and house, and — on
 * the engine path — where the engine is and its id for the sale. The client
 * never guesses, and never falls back to the other book when one is
 * unreachable: two live books for one lot can crown a winner who was outbid
 * in the other. Memory: feedback_engine_is_the_default_supabase_is_the_parachute.
 *
 * Cached per client and lot: a sale's path is fixed once the engine takes it
 * or a bid lands. Before that the switch can still move it, so a caller that
 * is told "wrong book" (`place_bid`'s `bid_on_engine`, the engine's
 * `auction_not_published`) forgets the route and asks again.
 */
export type BidRoute =
  | { path: 'supabase' }
  | {
      path: 'engine';
      saleId: string;
      houseId: string;
      /** The engine's base URL in this environment — from the database, never baked into an app. */
      engineUrl: string;
      /** The engine's id for the sale. */
      engineAuctionId: string;
    };

export type BidRouteLookup =
  | {
      ok: true;
      route: BidRoute;
      /**
       * Set when the answer is `supabase` only because the caller is signed
       * out and may not ask (42501). Right for a BID — `place_bid` answers
       * `unauthenticated` — but not a fact about the sale, so the live-source
       * routing (`live-source.ts`) must not read it as one.
       */
      signedOut?: true;
    }
  | { ok: false; error: { message: string; code?: string } };

const SUPABASE: BidRoute = { path: 'supabase' };
const routes = new WeakMap<AuctisyncSupabase, Map<string, BidRoute>>();

function cacheFor(supabase: AuctisyncSupabase): Map<string, BidRoute> {
  let byLot = routes.get(supabase);
  if (byLot === undefined) {
    byLot = new Map();
    routes.set(supabase, byLot);
  }
  return byLot;
}

/** Narrow the RPC's JSON to a route; anything else is not an engine route. */
function toRoute(data: unknown): BidRoute {
  if (typeof data !== 'object' || data === null) return SUPABASE;
  const d = data as Record<string, unknown>;
  if (
    d.path === 'engine' &&
    typeof d.sale_id === 'string' &&
    typeof d.house_id === 'string' &&
    typeof d.engine_url === 'string' &&
    d.engine_url.length > 0 &&
    typeof d.engine_auction_id === 'string'
  ) {
    return {
      path: 'engine',
      saleId: d.sale_id,
      houseId: d.house_id,
      engineUrl: d.engine_url.replace(/\/+$/, ''),
      engineAuctionId: d.engine_auction_id,
    };
  }
  return SUPABASE;
}

/**
 * The route for a lot. `fresh` skips the cache.
 *
 * Two answers stand for `place_bid`, and both leave the decision with it:
 *   - a database without the switch yet (`PGRST202`, function not found) has
 *     no engine path — production until its migration is applied;
 *   - a caller who may not ask (signed out: the function is not granted to
 *     anon) is a caller `place_bid` will answer `unauthenticated`.
 * Every other error is a failed lookup, reported as such: nothing was sent
 * to either book, so the caller can say so plainly.
 */
export async function getBidRoute(
  supabase: AuctisyncSupabase,
  lotId: string,
  opts: { fresh?: boolean } = {},
): Promise<BidRouteLookup> {
  const byLot = cacheFor(supabase);
  if (opts.fresh !== true) {
    const hit = byLot.get(lotId);
    if (hit !== undefined) return { ok: true, route: hit };
  }
  const { data, error } = await supabase.rpc('get_bid_path', { p_lot_id: lotId });
  if (error) {
    const code = (error as { code?: string }).code;
    if (code === 'PGRST202') {
      // No switch in this database: it has no engine path, for the session.
      byLot.set(lotId, SUPABASE);
      return { ok: true, route: SUPABASE };
    }
    // Signed out — not remembered: the same client may sign in a moment later.
    if (code === '42501') return { ok: true, route: SUPABASE, signedOut: true };
    return { ok: false, error: { message: error.message, ...(code ? { code } : {}) } };
  }
  const route = toRoute(data);
  byLot.set(lotId, route);
  return { ok: true, route };
}

/** Drop a lot's cached route — after an answer that says it went to the wrong book. */
export function forgetBidRoute(supabase: AuctisyncSupabase, lotId: string): void {
  routes.get(supabase)?.delete(lotId);
}
